import { useEffect, useRef } from 'react';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import { initializeApp, getApps } from 'firebase/app';
import { apiCall } from '../api/client';

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY || '';

/**
 * Registers for push notifications and stores the FCM token on the provider record.
 * Shows in-app notifications when the app is in the foreground.
 */
export function usePushNotifications(enabled: boolean) {
  const registered = useRef(false);

  useEffect(() => {
    if (!enabled || registered.current || !VAPID_KEY) return;

    (async () => {
      try {
        const supported = await isSupported();
        if (!supported) return;

        // Register the service worker
        const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');

        const app = getApps()[0] || initializeApp({
          apiKey: "AIzaSyDy_R9N4LRpgTN8Da--49O2VD2R-_shiUQ",
          authDomain: "simplytelehealth.firebaseapp.com",
          projectId: "simplytelehealth",
          messagingSenderId: "609770255900",
          appId: "1:609770255900:web:02175d71201fa25943d1d9",
        });

        const messaging = getMessaging(app);

        // Request permission
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') return;

        // Get FCM token
        const token = await getToken(messaging, {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: registration,
        });

        if (token) {
          // Store token on provider record
          await apiCall('/providers/fcm-token', {
            method: 'PUT',
            body: { fcmToken: token },
          });
          registered.current = true;
        }

        // Handle foreground messages
        onMessage(messaging, (payload) => {
          const { title, body, url } = payload.data || {};
          if (!title) return;

          // Show browser notification even when app is in foreground
          if (Notification.permission === 'granted') {
            const notif = new Notification(title, {
              body: body || '',
              icon: '/img/logo.svg',
              tag: payload.data?.visitId || 'sms-visit',
              requireInteraction: true,
              silent: false,
            });
            notif.onclick = () => {
              window.focus();
              if (url) window.location.href = url;
              notif.close();
            };
          }
        });
      } catch (err) {
        console.warn('Push notification setup failed:', err);
      }
    })();
  }, [enabled]);
}
